import type { FunctionComponent } from "react";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { TrafficGrowthComparison } from "app/components/React/TrafficGrowthComparison/TrafficGrowthComparison";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { PdfExportService } from "app/services/PdfExportService";
import { useDomainsFavicons } from "app/services/sitesService/hooks/useDomainsFavicons";
import type { IDomainFavicon } from "app/services/sitesService/types";
import { allTrackers } from "app/services/track/track";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { ButtonsContainer } from "pro-features/components/Rule/src/styledComponents";
import { FlexColumn, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { StyledBoxSubtitleWrapper } from "../StyledComponents";

import { TitleContainer, StyledHeaderTitle, ChartContainer, LoaderContainer } from "./StyledComponents";

const EMPTY_FAVICONS_LIST: IDomainFavicon[] = [];

interface ITotalVisitsGrowthProps {
    data: any;
    keys: [];
    isLoading?: boolean;
    excelUrl?: string;
    showGAApprovedData: boolean;
}

export const TotalVisitsGrowth: FunctionComponent<ITotalVisitsGrowthProps> = (props) => {
    const { data, isLoading, excelUrl, showGAApprovedData } = props;

    const services = React.useMemo(
        () => ({
            swNavigator: proNavigator,
            i18n: translate,
        }),
        [],
    );

    const chartRef = useRef<HTMLElement>(null); // for download png
    const { params } = useCurrentStateAndParams();
    const apiParams = services.swNavigator.getApiParams(params);
    const [isEmpty, setIsEmpty] = useState(true);
    const { duration, comparedDuration, country, webSource } = params;

    const keysArr = useMemo(() => {
        return params?.key ? (params.key as string).split(",").filter(Boolean) : [];
    }, [params?.key]);

    const { data: faviconsList = EMPTY_FAVICONS_LIST } = useDomainsFavicons(keysArr, {
        enabled: keysArr.length > 0,
    });

    // Utility:
    const getPNG = useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent("Gender_Distribution.png", "submit-ok", "Gender_Distribution.png");
        const offSetX = 0;
        const offSetY = 50;
        const fileName = `Website Analysis ${translate("traffic_and_engagement.growth_change.compare.title")}-(${
            apiParams.key
        })-(from: ${apiParams.from})-(to: ${apiParams.to})`;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            fileName,
            chartRef?.current?.offsetWidth + offSetX,
            chartRef?.current?.offsetHeight + offSetY,
        );
    }, [chartRef]);

    const onExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "website.analysis.traffic.and.engagement.compare.pop.excel.download",
            "submit-ok",
            "website.analysis.traffic.and.engagement.compare.pop.excel.download",
        );
    };

    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                {excelUrl ? (
                    <DownloadButtonMenu
                        Excel={true}
                        downloadUrl={excelUrl}
                        exportFunction={onExcelClick}
                        excelLocked={!downloadExcelPermitted}
                    />
                ) : null}
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
                <AddToDashboard
                    webSource="Total"
                    type="TrafficGrowthComparison"
                    metric="EngagementVisits"
                    modelType="fromWebsite"
                    filters={{
                        ShouldGetVerifiedData: showGAApprovedData,
                    }}
                />
            </ButtonsContainer>
        );
    };

    const faviconsByDomain = useMemo(() => {
        const map = new Map<string, string>();
        faviconsList.forEach((item) => {
            map.set(item.Domain?.toLowerCase(), item.Favicon);
        });
        return map;
    }, [faviconsList]);

    const chartData = useMemo(() => {
        if (!data?.data?.Data || isLoading) {
            return undefined;
        }

        return data.data.Data.map((siteData, index) => {
            return {
                Change: siteData?.Change,
                ComparedPeriodVisits: siteData?.ComparedPeriodVisits,
                Domain: siteData?.Domain,
                OriginalPeriodVisits: siteData?.OriginalPeriodVisits,
                color: CHART_COLORS.chartMainColors[index],
                image: faviconsByDomain.get(siteData?.Domain?.toLowerCase()),
            };
        });
    }, [data, isLoading, faviconsByDomain]);

    useEffect(() => {
        if (chartData) {
            const hasData = chartData.some(
                (siteData) => siteData?.ComparedPeriodVisits && siteData?.OriginalPeriodVisits,
            );
            setIsEmpty(!hasData);
        }
    }, [chartData]);

    const isLoadingChartData = isLoading || !chartData;

    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration, "TrafficAndEngagement"),
        [duration, comparedDuration],
    );

    const getViewData = useMemo(() => {
        const { forWidget } = durationObject;
        return {
            duration: forWidget,
            isDashboard: services.swNavigator.isDashboard(),
        };
    }, [duration, comparedDuration]);

    const subtitleFilters = React.useMemo(() => {
        const { from, to, compareFrom, compareTo } = durationObject?.raw;
        return [
            {
                filter: "date",
                value: {
                    from: compareFrom?.valueOf(),
                    to: compareTo?.valueOf(),
                    useRangeDisplay:
                        duration !== "28d" && compareFrom?.format("YYYY-MM") !== compareTo?.format("YYYY-MM"),
                },
            },
            {
                filter: "date",
                value: {
                    from: from?.valueOf(),
                    to: to?.valueOf(),
                    useRangeDisplay: duration !== "28d" && from?.format("YYYY-MM") !== to?.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ];
    }, [durationObject, country, webSource, duration]);

    return (
        <ChartContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={services.i18n("traffic_and_engagement.growth_change.compare.title.tooltip")}>
                            {services.i18n("traffic_and_engagement.growth_change.compare.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <StyledBoxSubtitleWrapper>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitleWrapper>
                </FlexColumn>
                <RightFlexRow>{getUtilityButtons()}</RightFlexRow>
            </TitleContainer>
            {isLoading || isLoadingChartData ? (
                <LoaderContainer>
                    <WidgetLoader />
                </LoaderContainer>
            ) : (
                <>
                    {isEmpty ? (
                        <TableNoData
                            icon="no-data"
                            messageTitle={translate("global.nodata.notavilable")}
                        />
                    ) : (
                        <TrafficGrowthComparison
                            data={chartData}
                            viewData={getViewData}
                        />
                    )}
                </>
            )}
        </ChartContainer>
    );
};
